// Pure UA/platform classifier - the canonical source of the "which store does
// this visitor get" decision shared by the web SPA and (as a verbatim mirror)
// apps/web/public/app-banner.js, which is copied by Vite and cannot import.
// A conformance test (apps/web/functions/__tests__/app-banner.test.ts) runs the
// same UA case table through BOTH copies so they cannot drift.
//
// Zero-dep leaf on purpose: the marketing bundle deep-imports
// `@verso/shared/src/utils/platform-classify` (like `constants/store-urls`) so
// it never pulls the zod-heavy barrel.

export interface PlatformClassification {
  isAndroid: boolean
  isIOS: boolean
  isGenuineIOSSafari: boolean
  showCustomBanner: boolean
}

export function classifyPlatform(
  ua: string | null | undefined,
  maxTouchPoints: number | null | undefined
): PlatformClassification {
  const userAgent = ua || ''
  const isAndroid = /Android/i.test(userAgent)
  // iPadOS 13+ Safari reports a desktop "Macintosh" UA; the only reliable tell
  // that it is really an iPad (not a Mac) is a touch-capable "Mac" - real Macs
  // report 0 or 1 touch points, an iPad reports 5.
  const isIpadDesktopUA = /Macintosh/i.test(userAgent) && (maxTouchPoints || 0) > 1
  const isIOS = /iP(hone|od|ad)/i.test(userAgent) || isIpadDesktopUA

  // The native Apple banner renders ONLY in genuine iOS Safari. Every other iOS
  // browser (Chrome/CriOS, Firefox/FxiOS, Edge/EdgiOS, Opera/OPiOS, Google/GSA)
  // and every in-app WKWebView (Instagram, Facebook, Snapchat, Pinterest, ...)
  // misses it. Rather than chase an app denylist, key off the one thing genuine
  // iOS Safari has that the others don't: a `Version/<n>` token paired with
  // `Safari`.
  const isGenuineIOSSafari =
    isIOS &&
    / Version\/\d/i.test(userAgent) &&
    /Safari/i.test(userAgent) &&
    !/(CriOS|FxiOS|EdgiOS|OPiOS|GSA)/i.test(userAgent)

  const showCustomBanner = (isAndroid || isIOS) && !isGenuineIOSSafari
  return { isAndroid, isIOS, isGenuineIOSSafari, showCustomBanner }
}
